import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';

import { cloneDeep, defaults, isEqual } from 'lodash';

import { Action } from '../action/action';
import { EmptyStateConfig } from './empty-state-config';

/**
 * Component for rendering an empty state.
 *
 * Usage:
 * <code><pre>
 * // Individual module import
 * import { EmptyStateModule } from 'patternfly-ng/empty-state';
 * // Or
 * import { EmptyStateModule } from 'patternfly-ng';
 *
 * &#64;NgModule({
 *   imports: [EmptyStateModule,...]
 * })
 * export class AppModule(){}
 * </pre></code>
 *
 * Optional:
 * <code><pre>
 * import { EmptyStateConfig } from 'patternfly-ng/empty-state';
 * </pre></code>
 */

export class EmptyStateComponent implements DoCheck, OnInit {
  /**
   * The empty state config containing component properties
   */
   config: EmptyStateConfig;

  /**
   * The event emitted when an action is selected
   */
   onActionSelect = new EventEmitter();

  private defaultConfig = {
    title: 'No Items Available'
  } as EmptyStateConfig;
  private prevConfig: EmptyStateConfig;

  /**
   * The default constructor
   */
  constructor() {
  }

  // Initialization

  /**
   * Setup component configuration upon initialization
   */
  ngOnInit(): void {
    this.setupConfig();
  }

  /**
   * Check if the component config has changed
   */
  ngDoCheck(): void {
    // Do a deep compare on config
    if (!isEqual(this.config, this.prevConfig)) {
      this.setupConfig();
    }
  }

  /**
   * Set up default config
   */
  protected setupConfig(): void {
    if (this.config !== undefined) {
      defaults(this.config, this.defaultConfig);
    } else {
      this.config = cloneDeep(this.defaultConfig);
    }
    this.prevConfig = cloneDeep(this.config);
  }

  // Private

  private handleAction(action: Action): void {
    if (action && action.disabled !== true) {
      this.onActionSelect.emit(action);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  encapsulation: ViewEncapsulation.None,
  selector: 'pfng-empty-state',
  template: `<div class="blank-slate-pf"><div *ngIf="config.iconStyleClass" class="blank-slate-pf-icon"><span class="{{config.iconStyleClass}}"></span></div><h1 id="title">{{config.title}}</h1><p id="info" *ngIf="config.info !== undefined">{{config.info}}</p><p id="helpLink" *ngIf="config.helpLink !== undefined">{{config.helpLink.text}} <a href="{{config.helpLink.url}}">{{config.helpLink.hypertext}}</a>.</p><div *ngIf="config.actions?.primaryActions?.length > 0" class="blank-slate-pf-main-action"><button *ngFor="let action of config.actions.primaryActions" class="btn btn-primary btn-lg {{action.styleClass}}" title="{{action.tooltip}}" [disabled]="action.disabled" [ngClass]="{'disabled': action.disabled, 'hidden': action.visible === false}" (click)="handleAction(action)"><div *ngIf="action.template; then showButtonTemplate else showButton"></div><ng-template #showButtonTemplate let-action="action" [ngTemplateOutlet]="action.template" [ngTemplateOutletContext]="{ action: action }"></ng-template><ng-template #showButton>{{action.title}}</ng-template></button></div><div class="blank-slate-pf-secondary-action {{config.actions?.moreActionsStyleClass}}" [ngClass]="{'hidden': config.actions?.moreActionsVisible === false}" *ngIf="config.actions?.moreActions?.length > 0"><button *ngFor="let action of config.actions.moreActions" class="btn btn-default {{action.styleClass}}" title="{{action.tooltip}}" [disabled]="action.disabled" [ngClass]="{'disabled': config.actions?.moreActionsDisabled, 'hidden': action.visible === false}" (click)="handleAction(action)">{{action.title}}</button></div></div>`
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'config': [{ type: Input },],
'onActionSelect': [{ type: Output, args: ['onActionSelect', ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
